// Resolve the canvas (editor) origin from the current workspace host.
// Mirrors the env tiers in server/packages/channels/channels.json:
//   localhost           → http://canvas.localhost:1355
//   next.my.symbols.app → https://canvas.next.symbols.app
//   dev.my.symbols.app  → https://canvas.dev.symbols.app
//   …<tier>.my.symbols.app → https://canvas.<tier>.symbols.app
//   my.symbols.app (prod) → https://edit.symbols.app
//
// Use this instead of hardcoding `https://edit.symbols.app/…` anywhere a
// workspace surface needs to deep-link into the editor (org/project create,
// open-in-canvas, etc.).
export const canvasOrigin = () => {
  if (typeof window === 'undefined') return 'https://edit.symbols.app'
  const host = window.location.hostname
  if (host.endsWith('.localhost') || host === 'localhost') {
    return 'http://canvas.localhost:1355'
  }
  // Tier subdomain on .my.symbols.app: split off the `my.` prefix and
  // route to canvas under the same tier.
  const m = host.match(/^([a-z0-9-]+)\.my\.symbols\.app$/i)
  if (m) return `https://canvas.${m[1]}.symbols.app`
  if (host === 'my.symbols.app') return 'https://edit.symbols.app'
  return 'https://edit.symbols.app'
}

export const canvasUrl = (path = '/') => {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${canvasOrigin()}${p}`
}

// Resolve the workspace (intranet shell) origin from the current host.
// Symmetric to `canvasOrigin()` — used by canvas + preview surfaces that
// need to deep-link back into the workspace (Workspace navbar button on
// the canvas, "Back to workspace" links, profile links, cross-domain
// signin redirects from preview, etc.).
//
//   *.localhost                 → http://my.localhost:1355
//   canvas.<tier>.symbols.app   → https://<tier>.my.symbols.app
//   <tier>.preview.symbols.app  → https://<tier>.my.symbols.app
//   preview.symbols.app         → https://my.symbols.app
//   edit.symbols.app (prod)     → https://my.symbols.app
export const workspaceOrigin = () => {
  if (typeof window === 'undefined') return 'https://my.symbols.app'
  const host = window.location.hostname
  if (host.endsWith('.localhost') || host === 'localhost') {
    return 'http://my.localhost:1355'
  }
  // Tier subdomain on .symbols.app: e.g. canvas.next.symbols.app → next.my.symbols.app.
  const m = host.match(/^canvas\.([a-z0-9-]+)\.symbols\.app$/i)
  if (m) return `https://${m[1]}.my.symbols.app`
  // Preview tier subdomain: <tier>.preview.symbols.app → <tier>.my.symbols.app.
  // Used by the preview surface's PRIVATE-project gate to hand off to
  // the same-tier workspace shell for sign-in.
  const pm = host.match(/^([a-z0-9-]+)\.preview\.symbols\.app$/i)
  if (pm) return `https://${pm[1]}.my.symbols.app`
  if (host === 'preview.symbols.app') return 'https://my.symbols.app'
  if (host === 'edit.symbols.app') return 'https://my.symbols.app'
  // Any my.<tier>.symbols.app stays — already on the workspace shell.
  if (/\bmy\.[a-z0-9.-]+\.symbols\.app$/i.test(host) || host === 'my.symbols.app') {
    return `${window.location.protocol}//${host}`
  }
  return 'https://my.symbols.app'
}

export const workspaceUrl = (path = '/') => {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${workspaceOrigin()}${p}`
}
